import React, { Suspense, lazy } from 'react';
import { ArrowRight, ChevronDown } from 'lucide-react';
import AnimatedGradientText from './AnimatedGradientText';
import { useLang } from './LanguageProvider';
import translations from '../translations';

/* Küre bileşeni tembel yüklenir ki hero metni anında çizilsin, cobe arka
   planda insin. Eski Globe3D.js dosyası yerinde duruyor ama artık
   çağrılmıyor — yerini 21st.dev "cobe-globe-pulse" uyarlaması aldı. */
const KurePulse = lazy(() => import('./KurePulse'));

/* Lightweight placeholder shown while KurePulse (cobe) loads */
function GlobePlaceholder() {
  return (
    <div className="w-full h-full flex items-center justify-center">
      <div
        className="rounded-full"
        style={{
          width: '60%',
          paddingBottom: '60%',
          background: 'radial-gradient(circle at 35% 35%, rgba(6, 182, 212, 0.15), rgba(139, 92, 246, 0.08), transparent 70%)',
          border: '1px solid rgba(6, 182, 212, 0.1)',
          boxShadow: '0 0 80px rgba(6, 182, 212, 0.08)',
          animation: 'pulse 2s ease-in-out infinite',
        }}
      />
    </div>
  );
}

/* CRM'in gerçek pazaryeri listesi (crm-whowsell/src/lib/marketplaces.ts).
   N11 kaldırıldı — CRM'de böyle bir pazaryeri yok, desteklenmeyen bir
   kanalı vaat ediyordu. Shopify ve Shopee eklendi. Globe3D.js'teki
   yörünge etiketleriyle aynı liste. */
const supportedMarketplaces = ['Amazon', 'Trendyol', 'Hepsiburada', 'Shopify', 'Walmart', 'eBay', 'Etsy', 'Shopee'];

export default function Hero() {
  const { lang } = useLang();
  const t = translations[lang].hero;

  const scrollToProducts = (e) => {
    e.preventDefault();
    document.getElementById('products')?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <section data-testid="hero-section" id="hero" className="relative min-h-[92vh] flex items-center overflow-hidden dark:bg-[#020617] bg-[#edf1f5]">
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute inset-0" style={{ background: 'radial-gradient(1400px at 65% 35%, rgba(6, 182, 212, 0.12), transparent 50%), radial-gradient(1000px at 25% 75%, rgba(139, 92, 246, 0.08), transparent 50%)' }} />
        <div className="absolute inset-0 opacity-[0.03]" style={{ backgroundImage: 'linear-gradient(rgba(6, 182, 212, 0.3) 1px, transparent 1px), linear-gradient(90deg, rgba(6, 182, 212, 0.3) 1px, transparent 1px)', backgroundSize: '60px 60px' }} />
      </div>

      <div data-testid="hero-3d-canvas" className="absolute top-0 right-[-5%] w-[65%] h-full z-0 hidden md:block" style={{ pointerEvents: 'auto', background: 'transparent' }}>
        <Suspense fallback={<GlobePlaceholder />}>
          <KurePulse erisimBaslik={t.supported} />
        </Suspense>
      </div>

      <div className="relative z-10 max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-20 sm:py-28 lg:py-36 w-full pointer-events-none">
        <div className="max-w-lg lg:max-w-xl pointer-events-auto">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full dark:bg-cyan-500/10 bg-cyan-50 border dark:border-cyan-500/20 border-cyan-200 mb-6 animate-fade-in">
            <span className="w-1.5 h-1.5 rounded-full bg-cyan-500 animate-pulse" />
            <span className="font-mono text-[10px] tracking-[0.18em] uppercase text-cyan-500">{t.badge}</span>
          </div>

          <h1 className="text-4xl sm:text-5xl lg:text-7xl font-extrabold tracking-tight mb-6 leading-[0.95]">
            <AnimatedGradientText as="span">{t.title}</AnimatedGradientText>
          </h1>

          <p className="text-base sm:text-lg dark:text-white/60 text-slate-600 leading-relaxed mb-10 max-w-xl">{t.desc}</p>

          <div className="flex flex-wrap gap-4">
            <a data-testid="hero-primary-cta" href="https://crm.whowsell.com/" target="_blank" rel="noopener noreferrer"
              className="wh-cta wh-cta--buyuk group bg-gradient-to-b from-cyan-400 to-cyan-500 hover:from-cyan-300 hover:to-cyan-400 text-slate-950 transition-all shadow-[0_0_0_1px_rgba(6,182,212,0.35),0_10px_30px_rgba(6,182,212,0.25)] active:scale-[0.97]">
              {t.cta1}
              <ArrowRight className="w-4 h-4 group-hover:translate-x-0.5 transition-transform" />
            </a>
            <button data-testid="hero-secondary-cta" onClick={scrollToProducts}
              className="wh-cta wh-cta--buyuk dark:bg-white/[0.06] bg-white/80 dark:text-white text-slate-700 dark:border-white/10 border-slate-200 border hover:dark:bg-white/[0.12] hover:bg-white transition-all active:scale-[0.97] backdrop-blur-sm">
              {t.cta2}
            </button>
          </div>

          <div className="md:hidden mt-10 h-[280px] rounded-2xl overflow-hidden" style={{ pointerEvents: 'auto' }}>
            <Suspense fallback={<GlobePlaceholder />}>
              <KurePulse erisimBaslik={t.supported} />
            </Suspense>
          </div>

          <div className="mt-14 flex flex-wrap gap-x-6 gap-y-2 items-center">
            <span className="text-xs dark:text-white/30 text-slate-400 font-medium">{t.supported}</span>
            <div className="flex flex-wrap gap-x-5 gap-y-1">
              {supportedMarketplaces.map(mp => (
                <span key={mp} className="text-xs font-semibold dark:text-white/25 text-slate-400 hover:dark:text-white/50 hover:text-slate-600 transition-colors tracking-wide">{mp}</span>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Scroll down chevron */}
      <div className="absolute bottom-6 left-1/2 -translate-x-1/2 z-20">
        <button
          onClick={() => document.getElementById('products')?.scrollIntoView({ behavior: 'smooth' })}
          className="w-10 h-10 rounded-full flex items-center justify-center dark:bg-white/[0.08] bg-slate-900/[0.08] dark:border-white/10 border-slate-300 border backdrop-blur-sm transition-all duration-300 hover:dark:bg-white/[0.15] hover:bg-slate-900/[0.15] hover:scale-110 animate-bounce-slow cursor-pointer"
          aria-label="Scroll down"
        >
          <ChevronDown className="w-5 h-5 dark:text-white/50 text-slate-500" />
        </button>
      </div>
    </section>
  );
}
